'use client'
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import type { School } from '@/lib/api/schools'
import { useAuth, useUser } from '@clerk/nextjs'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import type { CapabilityKey } from '@school-journey/shared-types'
import {
  buildViewerAccess,
  holds,
  membershipPermissions,
  type AccessRequirement,
  type ViewerAccess
} from '@/lib/access/viewer-access'
import { backendGET } from '@/utils/backend'
import { activeSchoolUserKeys } from '@/lib/query/keys/active-school-user'
import { schoolDetailKeys } from '@/lib/query/keys/school-detail'
import { STALE_TIME } from '@/lib/query/stale-times'
import { cancelForSchoolChange, onActiveSchoolChanged, refetchLegacyAfterSchoolChange } from '@/lib/query/school-switch'
import {
  ACTIVE_SCHOOL_COOKIE,
  ACTIVE_SCHOOL_NAME_COOKIE,
  ACTIVE_SCHOOL_SNAPSHOT_COOKIE,
  getBrowserCookie,
  clearBrowserCookie,
  writeActiveSchoolCookies
} from '@/lib/utils/cookies'

interface SchoolContextType {
  currentSchool: School | null
  changeSchool: (school: School) => Promise<void>
  isChangingSchool: boolean
  isLoadingSchool: boolean
  isSuperAdmin: boolean
  /**
   * Per-school user CUID (UsersOnSchools.publicId) for the active school.
   * Sourced from /v1/users/me filtered by the active school id.
   */
  activeSchoolUserId: string | null
  /** Per-school membership status (ACTIVE/PENDING/etc.). Sourced from /v1/users/me. */
  activeSchoolUserStatus: string | null
  /**
   * The viewer's school permissions at the active school plus their platform
   * permissions, built with the same `viewer-access.ts` functions the server
   * guard uses. Server-seeded, then refreshed from /v1/users/me. The nav and
   * client checks read it, the latter through `useViewerHolds`.
   */
  viewerAccess: ViewerAccess
  /** @deprecated retained for backwards compat; consumers should read `viewerAccess` */
  currentSchoolRole: null
}

const SchoolContext = createContext<SchoolContextType | undefined>(undefined)

/**
 * Picks the school to auto-select from a `/v1/users/me` response. Prefers an
 * ACTIVE membership (schoolUsers ordering is not ACTIVE-first, so a returning
 * multi-school admin must not be demoted onto a PENDING school), and falls back
 * to the first membership-with-a-school only when no ACTIVE one exists — which
 * seeds the cookie for a brand-new school's first admin (onboarded as PENDING)
 * so `x-school-id` is sent and the dashboard escapes the HTTP 428 loop.
 * Returns null when no membership carries a school.
 */
function firstMembershipSchool(
  result: { data?: { schoolUsers?: unknown } | null } | null
): { id: string; name: string } | null {
  const schoolUsers = (result?.data?.schoolUsers ?? []) as Array<{
    status?: string | null
    school?: { id: string; name: string } | null
  }>
  const match = schoolUsers.find(su => su.school?.id && su.status === 'ACTIVE') ?? schoolUsers.find(su => su.school?.id)
  return match?.school ?? null
}

/**
 * The school the active-school cookies describe, or null without both an id
 * and a name. The switcher's JSON snapshot fills the other fields only when it
 * describes the same school: a snapshot left by an earlier session must never
 * override the id cookie another writer (the verify screen) has since set. The
 * id and name cookies always win.
 */
function readCookieSchool(): School | null {
  const storedId = getBrowserCookie(ACTIVE_SCHOOL_COOKIE)
  const storedName = getBrowserCookie(ACTIVE_SCHOOL_NAME_COOKIE)
  if (!storedId || !storedName) return null
  const snapshot = parseSchoolSnapshot(getBrowserCookie(ACTIVE_SCHOOL_SNAPSHOT_COOKIE))
  const sameSchoolSnapshot = snapshot?.id === storedId ? snapshot : null
  return { ...sameSchoolSnapshot, id: storedId, name: storedName } as School
}

function parseSchoolSnapshot(storedSchool: string | null): Partial<School> | null {
  if (!storedSchool) return null
  try {
    return JSON.parse(storedSchool) as Partial<School>
  } catch {
    // ignore malformed cache
    return null
  }
}

export function SchoolProvider({
  children,
  initialIsSuperAdmin = false,
  initialActiveSchoolUserStatus = null,
  initialActiveSchoolUserId = null,
  initialActiveSchoolPermissions = null
}: {
  children: React.ReactNode
  initialIsSuperAdmin?: boolean
  /**
   * Server-rendered seed values so the first client paint matches the
   * server view (no admin-nav flash from member → admin during /v1/users/me
   * hydration). Layouts pass `getActiveSchool()` data here.
   */
  initialActiveSchoolUserStatus?: string | null
  initialActiveSchoolUserId?: string | null
  /** `getActiveSchool().permissions`; seeds `viewerAccess`. */
  initialActiveSchoolPermissions?: CapabilityKey[] | null
}) {
  const { user, isLoaded: isUserLoaded } = useUser()
  const { getToken, userId } = useAuth()
  const queryClient = useQueryClient()
  const router = useRouter()
  const [currentSchool, setCurrentSchool] = useState<School | null>(null)
  const [isChangingSchool, setIsChangingSchool] = useState(false)
  const [isLoadingSchool, setIsLoadingSchool] = useState(true)
  // Bumped in the same tick as `setCurrentSchool` by a switch or a membership
  // resolve (never by hydration from the cookie), so the effect below refetches
  // legacy queries once per switch, after the new school has rendered.
  const [legacyRefetchRequest, setLegacyRefetchRequest] = useState(0)
  const currentSchoolId = currentSchool?.id ?? null
  // The school last written, read by `changeSchool` so back-to-back switches
  // (A→B→C before a re-render) treat B, not A, as the previous school. Set
  // wherever `setCurrentSchool` is called.
  const currentSchoolIdRef = useRef<string | null>(null)

  // Initialize current school from cookie or Clerk user metadata
  useEffect(() => {
    if (!isUserLoaded) return

    setIsLoadingSchool(true)

    // Try cookie first (fastest)
    const cookieSchool = readCookieSchool()
    if (cookieSchool) {
      currentSchoolIdRef.current = cookieSchool.id
      setCurrentSchool(cookieSchool)
      setIsLoadingSchool(false)
      return
    }

    // Fall back to Clerk user metadata (persistedSchool)
    const persistedSchool = user?.unsafeMetadata?.persistedSchool as
      { id: string; name: string; slug?: string } | undefined

    if (persistedSchool?.id) {
      currentSchoolIdRef.current = persistedSchool.id
      setCurrentSchool({
        id: persistedSchool.id,
        name: persistedSchool.name
      } as School)
      // Sync to cookie
      writeActiveSchoolCookies({ id: persistedSchool.id, name: persistedSchool.name })
      setIsLoadingSchool(false)
      return
    }

    // No cookie and no persisted metadata — this is the case that wedged
    // brand-new schools at HTTP 428. A new school's first admin is onboarded
    // as PENDING (not ACTIVE), so the other cookie writers all fail-closed,
    // `x-school-id` is never sent, and every school-bound request 428s forever.
    // Resolve the active school directly from the user's memberships and
    // persist it, taking the FIRST membership regardless of status (PENDING or
    // ACTIVE) so a brand-new admin always gets a cookie on first login.
    let cancelled = false
    void (async () => {
      try {
        const token = await getToken()
        if (cancelled || !token) return
        const result = await backendGET({ path: '/v1/users/me', accessToken: token })
        const resolved = firstMembershipSchool(result)
        if (cancelled || !resolved) return
        currentSchoolIdRef.current = resolved.id
        setCurrentSchool({ id: resolved.id, name: resolved.name } as School)
        writeActiveSchoolCookies({ id: resolved.id, name: resolved.name })
        // Mirror the switcher's changeSchool path: refresh server components and
        // run the school-switch sweep. Without this, queries that already errored
        // with 428 (before the cookie existed) stay settled in error state — the
        // switcher's auto-select bails once currentSchool is set, so it never
        // fires this refetch. (changeSchool is declared after this effect, so it
        // can't be referenced here; call the primitives directly.) No previous
        // school, so nothing to cancel.
        onActiveSchoolChanged(queryClient, null)
        setLegacyRefetchRequest(n => n + 1)
        router.refresh()
      } catch (error) {
        console.error('[SchoolContext] Failed to resolve active school from memberships:', error)
      } finally {
        if (!cancelled) setIsLoadingSchool(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isUserLoaded, user, getToken, router, queryClient])

  const changeSchool = useCallback(
    async (school: School) => {
      const previousSchoolId = currentSchoolIdRef.current
      // Re-selecting the active school keeps its cache: nothing to cancel,
      // clear or refetch.
      const isSwitch = school.id !== previousSchoolId
      setIsChangingSchool(true)

      try {
        // Stop in-flight fetches that could read the new cookie under an old
        // key, before the new school is written anywhere. Not awaited:
        // `Query.cancel` cancels synchronously, and the cookie write below
        // must stay before the first await (the switcher navigates right after
        // calling this and relies on the new `x-school-id`).
        if (isSwitch) void cancelForSchoolChange(queryClient, previousSchoolId)

        // Update local state
        currentSchoolIdRef.current = school.id
        setCurrentSchool(school)

        // Persist to cookie (used by configureHeaders for x-school-id and by
        // server pages via Next.js cookies()). The full-school snapshot lets
        // avatar/logo render immediately after reload, before the detail query
        // has a chance to refetch.
        writeActiveSchoolCookies(school, school)

        // Clear legacy data before the Clerk round-trip below, so no window
        // shows school A's rows under school B's name. The effect keyed on
        // `legacyRefetchRequest` refetches them once the new school renders.
        if (isSwitch) {
          onActiveSchoolChanged(queryClient, previousSchoolId)
          setLegacyRefetchRequest(n => n + 1)
        }

        // Persist to Clerk user metadata for cross-device sync
        if (user) {
          await user.update({
            unsafeMetadata: {
              ...user.unsafeMetadata,
              persistedSchool: {
                id: school.id,
                name: school.name
              }
            }
          })
        }

        // Refresh server components (they were rendered against the old school
        // context). This avoids the avatar/logo flicker that a full page reload
        // causes.
        router.refresh()
      } catch (error) {
        console.error('[SchoolContext] Failed to update school selection:', error)
      } finally {
        setIsChangingSchool(false)
      }
    },
    [queryClient, router, user]
  )

  // Resolve per-school user CUID + status + permissions from /v1/users/me
  // filtered by the active school id.
  const { data: activeSchoolUser = null } = useQuery({
    queryKey: activeSchoolUserKeys.detail(currentSchoolId, userId),
    queryFn: async (): Promise<{
      id: string
      status: string | null
      permissions: CapabilityKey[]
    } | null> => {
      if (!currentSchool?.id) return null
      const token = await getToken()
      if (!token) return null
      const result = await backendGET({
        path: '/v1/users/me',
        accessToken: token
      })
      const schoolUsers = (result?.data?.schoolUsers ?? []) as Array<{
        id: string
        status?: string | null
        roles?: { isAdmin?: boolean }
        permissions?: string[]
        school?: { id: string } | null
      }>
      const match = schoolUsers.find(su => su.school?.id === currentSchool.id)
      if (!match) return null
      return {
        id: match.id,
        status: match.status ?? null,
        permissions: membershipPermissions(match)
      }
    },
    enabled: !!userId && !!currentSchool?.id,
    staleTime: 5 * 60 * 1000
  })

  /**
   * Hydrate the active school's FULL record from `GET /v1/schools/:id`.
   *
   * Three of the four `currentSchool` writers above can only produce
   * `{ id, name } as School` — the cookie shortcut, the Clerk-metadata fallback
   * and the `/v1/users/me` membership resolve. The `as School` cast means every
   * other field (`timezone`, `slug`, logos, …) reads as `undefined` with no type
   * error, and only `changeSchool` — reachable solely through the school
   * SWITCHER — ever carried a complete object. A single-school admin therefore
   * never had one.
   *
   * That is what shipped the MCDS "I select 11:30am, it shows 6:30am" bug: the
   * events builder read `currentSchool.timezone`, got `undefined`, silently fell
   * back to UTC, and stored Eastern wall-clocks as UTC instants. Filling the
   * record here fixes it for every context consumer rather than teaching one
   * feature to fetch its own timezone.
   *
   * Merged as DERIVED state rather than via `setCurrentSchool` so the fetch can
   * never feed the hydration effect back into itself. The cookie-seeded fields
   * stay first paint; the server response wins once it lands.
   */
  const { data: activeSchoolDetail = null } = useQuery({
    queryKey: schoolDetailKeys.detail(currentSchool?.id ?? null),
    queryFn: async (): Promise<Partial<School> | null> => {
      if (!currentSchool?.id) return null
      const token = await getToken()
      if (!token) return null
      const result = await backendGET({
        path: `/v1/schools/${currentSchool.id}`,
        accessToken: token,
        schoolId: currentSchool.id
      })
      // backendGET RESOLVES an `{ error }` envelope on HTTP failure instead of
      // throwing, so guard rather than trusting `data` to be present.
      if (result?.error || !result?.data) return null
      return result.data as Partial<School>
    },
    enabled: !!userId && !!currentSchool?.id,
    staleTime: STALE_TIME.long
  })

  // Runs after every child's effects in the commit that rendered the new
  // school, and after this component's own `useQuery` effects above (effects in
  // one component run in declaration order, so keep this below them). Each
  // mounted legacy observer is already on its new key (TanStack moves it in an effect), so no legacy
  // refetch can land under an old school's key.
  useEffect(() => {
    if (legacyRefetchRequest === 0) return
    void refetchLegacyAfterSchoolChange(queryClient)
  }, [legacyRefetchRequest, queryClient])

  // Detail wins over the cookie seed; `id`/`name` are pinned to the resolved
  // active school so a partial payload can never blank the identity the rest of
  // the app (and `x-school-id`) is keyed on.
  const hydratedSchool = React.useMemo(() => {
    if (!currentSchool) return null
    if (!activeSchoolDetail) return currentSchool
    return { ...currentSchool, ...activeSchoolDetail, id: currentSchool.id, name: currentSchool.name }
  }, [currentSchool, activeSchoolDetail])

  // Seed-then-hydrate: prefer the server-rendered seed until /v1/users/me
  // resolves, so the first paint matches the server view and admin-targeted
  // nav items don't flash from member → admin (SHOULD-1).
  const activeSchoolUserId = activeSchoolUser?.id ?? initialActiveSchoolUserId
  const activeSchoolUserStatus = activeSchoolUser?.status ?? initialActiveSchoolUserStatus
  const activeSchoolPermissions = activeSchoolUser ? activeSchoolUser.permissions : initialActiveSchoolPermissions
  const viewerAccess = React.useMemo(
    () => buildViewerAccess({ isSuperAdmin: initialIsSuperAdmin, schoolPermissions: activeSchoolPermissions ?? [] }),
    [initialIsSuperAdmin, activeSchoolPermissions]
  )

  return (
    <SchoolContext.Provider
      value={{
        currentSchool: hydratedSchool,
        changeSchool,
        isChangingSchool,
        isLoadingSchool,
        isSuperAdmin: initialIsSuperAdmin,
        activeSchoolUserId,
        activeSchoolUserStatus,
        viewerAccess,
        currentSchoolRole: null
      }}
    >
      {children}
    </SchoolContext.Provider>
  )
}

export function useSchool() {
  const context = useContext(SchoolContext)
  if (!context) {
    throw new Error('useSchool must be used within a SchoolProvider')
  }
  return context
}

/**
 * Whether the viewer holds `requirement` at the active school: the client
 * check for a button, dialog or affordance. A school requirement also passes
 * for a super-admin. Reads the same server-seeded `viewerAccess`, so the first
 * paint matches the server view. False outside a SchoolProvider.
 */
export function useViewerHolds(requirement: AccessRequirement): boolean {
  const viewerAccess = useSchoolOptional()?.viewerAccess
  return viewerAccess ? holds(viewerAccess, requirement) : false
}

/**
 * Variant of useSchool that returns null when called outside a
 * SchoolProvider. For pages that live outside the (core) layout (e.g.
 * demo routes) but still want to read the active-school context.
 */
export function useSchoolOptional(): SchoolContextType | null {
  return useContext(SchoolContext) ?? null
}

/**
 * The active school id for React code: the one client-side reader. Hooks pass
 * the same value to the query key and to the request.
 *
 * `SchoolProvider` fills `currentSchool` in an effect, after the first render;
 * until then the cookie it hydrates from is the same answer, so the first
 * render's queries still send `x-school-id` instead of caching a 428. Outside a
 * provider it is the cookie alone. `changeSchool` writes both in one tick.
 */
export function useActiveSchoolId(): string | null {
  return useSchoolOptional()?.currentSchool?.id ?? getBrowserCookie(ACTIVE_SCHOOL_COOKIE) ?? null
}

/**
 * Exported for tests or for consumers that need to clear the active-school
 * selection on logout. Keeps cookie key knowledge centralized.
 */
export function clearActiveSchoolCookies(): void {
  clearBrowserCookie(ACTIVE_SCHOOL_COOKIE)
  clearBrowserCookie(ACTIVE_SCHOOL_NAME_COOKIE)
  clearBrowserCookie(ACTIVE_SCHOOL_SNAPSHOT_COOKIE)
}
